'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import '../../static-page/staticStyle.scss';
import '../../static-page/commonAmongAllPagesStyles.scss';
import styles from './DownloadSemeru.module.css';
import type { DownloadRow, DownloadsData, RuntimeGroup } from '../data/types';

const LTS_SET = new Set([8, 11, 17, 21, 25]);

function htmlDecode(encoded: string): string {
  if (!encoded) return '';
  return encoded
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'");
}

function uniqueSorted<T extends string | number>(arr: T[]): T[] {
  return Array.from(new Set(arr)).sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  });
}

function getLicenseUrl(params: { edition: 'GPL' | 'IBM'; version: number; os: DownloadRow['os'] }): string {
  if (params.edition !== 'IBM') return 'https://openjdk.java.net/legal/gplv2+ce.html';

  const { version, os } = params;
  if (version === 11) {
    return os === 'z/OS' ? 'http://www.ibm.com/terms/?id=L-JPLW-CGKLX3' : 'http://www.ibm.com/terms/?id=L-PARM-C5ME22';
  }
  if (version === 17) {
    return os === 'z/OS' ? 'http://www.ibm.com/terms/?id=L-DYEY-TMFX2M' : 'http://www.ibm.com/terms/?id=L-PARM-C9S7PL';
  }
  if (version === 21) {
    return os === 'z/OS' ? 'http://www.ibm.com/terms/?id=L-HWZA-R5592K' : 'http://www.ibm.com/terms/?id=L-VUGR-8QMH3W';
  }
  if (version === 25) {
    return os === 'z/OS' ? '' : 'http://www.ibm.com/terms/?id=L-LYMG-GGTKT8';
  }
  return '';
}

function archOptionsForOS(os: DownloadRow['os'] | 'Any', version: number): Array<DownloadRow['arch'] | 'Any'> {
  if (os === 'Any') {
    return ['Any', 'x64', 'x86', 'ppc64le', 'ppc64', 's390x', 'aarch64'];
  }
  if (os === 'Linux') {
    return ['Any', 'x64', 'ppc64le', 's390x', 'aarch64'];
  }
  if (os === 'Windows') {
    const arr: Array<DownloadRow['arch']> = [];
    if (Number(version) === 8) arr.push('x86');
    arr.push('x64');
    return ['Any', ...arr];
  }
  if (os === 'macOS') {
    return ['Any', 'x64', 'aarch64'];
  }
  if (os === 'AIX') {
    return ['ppc64'];
  }
  if (os === 'z/OS') {
    return ['s390x'];
  }
  return ['Any'];
}

function runtimeKeys(row: DownloadRow): Array<keyof Pick<DownloadRow, 'jdk' | 'jre' | 'jextract'>> {
  const keys: Array<keyof Pick<DownloadRow, 'jdk' | 'jre' | 'jextract'>> = [];
  if (row.jdk && typeof row.jdk === 'object') keys.push('jdk');
  if (row.jre && typeof row.jre === 'object') keys.push('jre');
  if (row.jextract && typeof row.jextract === 'object') keys.push('jextract');
  return keys;
}

function optionEntries(runtimeObj: RuntimeGroup): Array<[string, RuntimeGroup[string]]> {
  return Object.keys(runtimeObj)
    .sort()
    .map((k) => [k, runtimeObj[k]]);
}

function formatVersionLabel(v: number): string {
  return LTS_SET.has(v) ? `Java ${v} (LTS)` : `Java ${v}`;
}

export type DownloadSemeruProps = {
  data: DownloadsData;
  isArchive?: boolean;
};

type ProductType = 'runtime' | 'jextract';

const DownloadSemeru: React.FC<DownloadSemeruProps> = ({ data, isArchive = false }) => {
  const searchParams = useSearchParams();

  const allVersions = useMemo(() => uniqueSorted(data.map((d) => d.version)), [data]);
  const defaultVersion = allVersions[allVersions.length - 1] ?? 17;

  const [productType, setProductType] = useState<ProductType>('runtime');
  const [edition, setEdition] = useState<'GPL' | 'IBM'>('GPL');
  const [version, setVersion] = useState<number>(defaultVersion);
  const [os, setOs] = useState<DownloadRow['os'] | 'Any'>('Any');
  const [arch, setArch] = useState<DownloadRow['arch'] | 'Any'>('Any');

  // Handle product type changes - set default version to 25 for Jextract
  useEffect(() => {
    if (productType === 'jextract') {
      setVersion(25);
    } else {
      setVersion(defaultVersion);
    }
  }, [productType, defaultVersion]);

  useEffect(() => {
    if (!searchParams) return;

    const qpLicense = searchParams.get('license');
    const qpVersion = searchParams.get('version');
    const qpOs = searchParams.get('os');
    const qpArch = searchParams.get('arch');

    if (qpLicense === 'GPL' || qpLicense === 'IBM') {
      setEdition(qpLicense);
    }
    if (qpVersion && allVersions.includes(Number(qpVersion))) {
      setVersion(Number(qpVersion));
    }
    if (qpOs && ['Linux', 'Windows', 'macOS', 'AIX', 'z/OS'].includes(qpOs)) {
      setOs(qpOs as DownloadRow['os']);
    }
    if (qpArch && ['x64', 'x86', 'ppc64le', 'ppc64', 's390x', 'aarch64'].includes(qpArch)) {
      setArch(qpArch as DownloadRow['arch']);
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const archOptions = useMemo(() => archOptionsForOS(os, version), [os, version]);

  useEffect(() => {
    if (!archOptions.includes(arch)) {
      setArch(archOptions[0] ?? 'Any');
    }
  }, [arch, archOptions]);

  const filteredRows = useMemo(() => {
    return data.filter((row) => {
      if (row.license !== edition) return false;
      if (Number(row.version) !== Number(version)) return false;
      if (os !== 'Any' && row.os !== os) return false;
      if (arch !== 'Any' && row.arch !== arch) return false;
      
      // Filter based on product type
      if (productType === 'jextract') {
        // Only show rows that have jextract data AND do not have jdk or jre
        const hasJextract = row.jextract && typeof row.jextract === 'object';
        const hasJdk = row.jdk && typeof row.jdk === 'object';
        const hasJre = row.jre && typeof row.jre === 'object';
        return hasJextract && !hasJdk && !hasJre;
      } else {
        // Only show rows that have jdk or jre data (runtime products)
        return (row.jdk && typeof row.jdk === 'object') || (row.jre && typeof row.jre === 'object');
      }
    });
  }, [data, edition, version, os, arch, productType]);

  return (
    <div className="static page">
      <div className="wp-content content semeru-runtime-downloads">
        <h3>{isArchive ? 'IBM Semeru Runtimes Archives' : 'IBM Semeru Runtimes Downloads'}</h3>
        {isArchive ? (
          <>
            <p className={styles.semeruParagraph}>
              This page contains previous, unsupported releases of IBM Semeru Runtimes Open Edition. These releases are maintained here for archival and historical purposes only and are no longer supported.
            </p>
            <p className={styles.semeruParagraph}>
              Unsupported versions may contain known security vulnerabilities. You can review details on known vulnerabilities by visiting the{' '}
              <a
                className={`${styles.semeruLink} bx--link bx--link--inline`}
                href="https://www.ibm.com/support/pages/semeru-runtimes-security-vulnerabilities"
                target="_blank"
                rel="noreferrer"
              >
                IBM Semeru Runtimes Security Vulnerabilities page
              </a>
              .
            </p>
            <p className={styles.semeruParagraph}>
              For supported and actively maintained versions of IBM Semeru Runtimes (including Long Term Support releases and Certified Editions), please visit the{' '}
              <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="/languages/java/semeru-runtimes/downloads/">
                IBM Semeru Runtimes Downloads page
              </a>
              .
            </p>
            <p className={styles.semeruParagraph}>
              To verify these packages, download our public keys and follow the instructions on how to{' '}
              <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://www.ibm.com/support/pages/semeru-runtimes-verification/" target="_blank" rel="noreferrer">
                verify IBM Semeru Runtimes.
              </a>
            </p>
          </>
        ) : (
          <>
            <p className={styles.semeruParagraph}>
              To verify these packages, download our public keys and follow the instructions on how to{' '}
              <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://www.ibm.com/support/pages/semeru-runtimes-verification/" target="_blank" rel="noreferrer">
                verify IBM Semeru Runtimes.
              </a>
            </p>

            <p className={styles.semeruParagraph}>
              <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://community.ibm.com/community/user/blogs/paul-arockiam/2026/05/13/jextract-for-ibm-semeru-runtimes-v250-is-published" target="_blank" rel="noreferrer">
              Pre-built binaries of jextract for IBM Semeru Runtimes v25.0 are published
              </a>
              , a tool which mechanically generates Java bindings from native library headers.
            </p>

            <p className={styles.semeruParagraph}>
              Looking for older unsupported releases? Visit the{' '}
              <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="/languages/java/semeru-runtimes/downloads/archives/">
                IBM Semeru Runtimes Archives page
              </a>
              .
            </p>

            <p className={styles.semeruParagraph}><strong>Additional resources:</strong></p>
            <ul>
            <li>
                <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://www.ibm.com/support/pages/semeru-runtimes-release-notes/" target="_blank" rel="noreferrer">
                  IBM Semeru Runtimes Release Notes
                </a>
                {' '}- Review what's new, fixed, and changed in each IBM Semeru Runtimes release.
              </li>
              <li>
                <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://www.ibm.com/support/pages/semeru-runtimes-getting-started/" target="_blank" rel="noreferrer">
                  Getting Started with IBM Semeru Runtimes
                </a>
                {' '}- Step-by-step guidance to install and configure IBM Semeru Runtimes on your platform.
              </li>
              <li>
                <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://www.ibm.com/support/pages/semeru-runtimes-support/" target="_blank" rel="noreferrer">
                  IBM Semeru Runtimes Support
                </a>
                {' '}- Learn about support options, service levels, and how to open a support case.
              </li>
              <li>
                <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://github.com/ibmruntimes/Semeru-Runtimes/issues" target="_blank" rel="noreferrer">
                  IBM Semmeru Runtimes GitHub Issues
                </a>
                {' '}- Report bugs, request features, or track known issues for IBM Semeru Runtimes.
              </li>
              <li>
                <a className={`${styles.semeruLink} bx--link bx--link--inline`} href="https://hub.docker.com/_/ibm-semeru-runtimes" target="_blank" rel="noreferrer">
                  IBM Semeru Runtimes on Docker Hub
                </a>
                {' '}- Pull official IBM Semeru Runtimes container images for use in Docker and Kubernetes environments.
              </li>
            </ul>
          </>
        )}

        <div className={styles.wrap}>
          <div className="row">
            {!isArchive && (
              <div className={styles.column}>
                <label>
                  <select
                    className="bx--select-input filter"
                    value={`${productType}-${edition}`}
                    onChange={(e) => {
                      const [newProductType, newEdition] = e.target.value.split('-') as [ProductType, 'GPL' | 'IBM'];
                      setProductType(newProductType);
                      setEdition(newEdition);
                    }}
                    aria-label="Product Edition"
                  >
                    <option value="runtime-GPL">IBM Semeru Runtime Open Edition</option>
                    <option value="runtime-IBM">IBM Semeru Runtime Certified Edition</option>
                    <option value="jextract-GPL">IBM Semeru Jextract Open Edition</option>
                    <option value="jextract-IBM">IBM Semeru Jextract Certified Edition</option>
                  </select>
                  <div className={styles.arrow}>ˇ</div>
                </label>
              </div>
            )}

            <div className={styles.column}>
              <label>
                <select  className="bx--select-input filter" value={String(version)} onChange={(e) => setVersion(Number(e.target.value))} aria-label="Java Version">
                  {allVersions.map((v) => (
                    <option key={v} value={v}>{formatVersionLabel(v)}</option>
                  ))}
                </select>
                <div className={styles.arrow}>ˇ</div>
              </label>
            </div>

            <div className={styles.column}>
              <label>
                <select className="bx--select-input filter" value={os} onChange={(e) => setOs(e.target.value as DownloadRow['os'] | 'Any')} aria-label="Operating System">
                  {(['Any', 'Linux', 'Windows', 'macOS', 'AIX', 'z/OS'] as const).map((v) => (
                    <option key={v} value={v}>{ v === 'Any' ? 'Operating System: Any' : `Operating System: ${v}` }</option>
                  ))}
                </select>
                <div className={styles.arrow}>ˇ</div>
              </label>
            </div>

            <div className={styles.column}>
              <label>
                <select className="bx--select-input filter" value={arch} onChange={(e) => setArch(e.target.value as DownloadRow['arch'] | 'Any')} aria-label="Architecture">
                  {archOptions.map((a) => (
                    <option key={a} value={a}>{ a === 'Any' ? 'Architecture: Any' : `Architecture: ${a}` }</option>
                  ))}
                </select>
                <div className={styles.arrow}>ˇ</div>
              </label>
            </div>
          </div>
        </div>

        <p></p>

        <table className="bx--data-table bx--data-table--no-border pevents">
          <thead>
            <tr>
              <th scope="col">
                <div className="bx--table-header-label">Version</div>
              </th>
              <th scope="col">
                <div className="bx--table-header-label">Operating System</div>
              </th>
              <th scope="col">
                <div className="bx--table-header-label">Architecture</div>
              </th>
              <th scope="col">
                <div className="bx--table-header-label">License</div>
              </th>
              <th scope="col">
                <div className="bx--table-header-label">Certified</div>
              </th>
              <th scope="col">
                <div className="bx--table-header-label"></div>
              </th>
            </tr>
          </thead>
          <tbody id="download-table">
            {filteredRows.map((row, idx) => {
              const isIBM = edition === 'IBM';
              const licenseText = isIBM ? 'IBM' : 'GPLv2+CE';
              const licenseUrl = getLicenseUrl({
                edition,
                version: Number(row.version),
                os: row.os,
              });

              const isLatest = String(row.latest) === 'true';
              const isEarly = String(row.earlyAccess) === 'true';

              const versionName = htmlDecode(row.name);

              return (
                <tr key={`${row.os}-${row.arch}-${idx}`}>
                  <td className={`${styles.version} pdate`}>
                    <span dangerouslySetInnerHTML={{ __html: versionName }} />
                    <br />
                    { isLatest && (
                      <>
                        <span className={styles.latest}>Latest</span>
                        <br />
                      </>
                    )}
                    { isEarly && <span className={styles.earlyAccess}>Early Access</span> }
                  </td>
                  <td className="ptitle">{row.os}</td>
                  <td>{row.arch}</td>
                  <td>{ licenseUrl ? ( <a href={licenseUrl} target="_blank" rel="noreferrer">{licenseText}</a> ) : ( licenseText ) }</td>
                  <td>{ isIBM ? 'Yes' : 'No' }</td>
                  <td>
                    <table className={styles.download}>
                      <tbody>
                        {(() => {
                          const rts = runtimeKeys(row);
                          return rts.map((rtKey, index) => {
                            const rt = row[rtKey]!;
                            const opts = optionEntries(rt);
                            const isLastRuntime = index === rts.length - 1;

                            return (
                              <tr key={rtKey}>
                                <td className={`${styles.tdDownload} ${styles.tdRuntime} ${isLastRuntime ? styles.tdDownloadBottom : ''}`}>{rtKey.toUpperCase()}</td>

                                {opts.map(([optKey, opt]) => {
                                  return (
                                    <td key={optKey} className={`${styles.download} ${styles.downloadWidth} ${isLastRuntime ? styles.tdDownloadBottom : ''}`}>
                                      { opt.downloadLink ? ( <a className={styles.download} href={opt.downloadLink}>{opt.displayName}</a> ) : ( opt.displayName )}
                                      { opt.checksum && ( <p className={`${styles.checksum} ${styles.download}`} onClick={() => alert(opt.checksum)}>SHA256</p> )}
                                      { opt.sig && ( <a className={styles.download} href={opt.sig}>signature</a> )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );

                          });
                        })()}
                      </tbody>
                    </table>
                  </td>
                </tr>
              );
            })}

            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.smallText}>
                  No downloads match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DownloadSemeru;
